<template>
    <DsPopover
        hide-arrow
        is-open
        class="inline-add-popover-menu"
        :style="popoverStyle"
    >
        <template #default>
            <div class="inline-add-popover-menu-content">
                <DsInputField
                    ref="searchInput"
                    v-model="searchText"
                    data-qa="search-input"
                    :label="searchPlaceholder"
                    class="element-search"
                >
                    <template #leading>
                        <DsIcon name="search" />
                    </template>
                    <template #trailing>
                        <span
                            v-show="searchText"
                            class="clear-icon"
                            data-qa="clear-icon"
                            @click="clearSearch"
                        >
                            <DsIcon name="x-circle-fill" />
                        </span>
                    </template>
                </DsInputField>
                <div class="element-list">
                    <div
                        v-if="displayWhens"
                        class="list-group"
                    >
                        <span
                            class="headline-4"
                            data-qa="when-header"
                        >
                            {{ $t('funnel.inlineBetweenMenu.when.trigger') }}
                        </span>
                        <DsListItem
                            v-for="element in whenElements"
                            :key="element.id"
                            :title="element.displayLabel"
                            class="element-list-item"
                            @click="onElementClick(element)"
                        >
                            <template #leading>
                                <img class="element-icon" :src="element.iconUrl" :alt="element.name" />
                            </template>
                        </DsListItem>
                    </div>
                    <div
                        v-if="thenElements.length > 0"
                        class="list-group"
                    >
                        <span
                            class="headline-4"
                            data-qa="then-header"
                        >
                            {{ $t('funnel.inlineBetweenMenu.then.trigger') }}
                        </span>
                        <DsListItem
                            v-for="element in thenElements"
                            :key="element.id"
                            :title="element.displayLabel"
                            data-qa="then-list"
                            class="element-list-item"
                            @click="onElementClick(element)"
                        >
                            <template #leading>
                                <img class="element-icon" :src="element.iconUrl" />
                            </template>
                        </DsListItem>
                    </div>
                </div>
            </div>
        </template>
    </DsPopover>
</template>

<script>
import { getIconPathByCellType } from '../utils/cell/automation-display-utils';
import {
    CELL_TYPE,
    WHEN_CELL_OPTIONS,
    THEN_CELL_OPTIONS,
    MAIN_CANVAS_ID,
    PROCESS_EMAIL_CONFIRM,
    SEQUENCE_START, VALID_INCOMING_FLOW,
} from '../utils/editor.constants';
import {
    isValid,
} from '../utils/cell/automation-cell-utils';
import {
    findOutgoingCells,
    isValidTimerNextFlow,
} from '../utils/cell/automation-edge-utils';

const OFFSET_BASE_X = 190;
const OFFSET_BASE_Y = 35;
const OFFSET_MULTIPLIER_X = 30;
const OFFSET_MULTIPLIER_Y = 5;
const BIG_CARD_THRESHOLD = 1.5;
const BIG_CARD_OFFSET_X = 216;
const BIG_CARD_OFFSET_X_SEQ = 160;
const BIG_CARD_OFFSET_X_START = 60;

export default {
    props: {
        selectedSequence: {
            type: String,
            required: true,
        },
        inlineBetweenAddData: {
            type: Object,
            required: true,
        },
        enabledCellTypes: {
            type: Array,
            required: true,
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
    },

    emits: ['add-cell-between'],

    data() {
        return {
            searchText: '',
        };
    },

    computed: {
        bigCardOffset() {
            if (this.isInSequence) {
                if (this.sourceElement.type === SEQUENCE_START) {
                    return BIG_CARD_OFFSET_X_START;
                }

                return BIG_CARD_OFFSET_X_SEQ;
            }

            return BIG_CARD_OFFSET_X;
        },

        isOutgoingCellAWhen() {
            const outgoingCells = findOutgoingCells(this.sourceElement.id, this.cells);

            if (outgoingCells.length === 0) {
                return false;
            }

            return outgoingCells.some((cell) => this.isAWhen(cell));
        },

        displayWhens() {
            return this.isAThen && !this.isInSequence && this.whenElements.length > 0 && !this.isOutgoingCellAWhen;
        },

        isAThen() {
            return this.type === CELL_TYPE.THEN;
        },

        isInSequence() {
            return this.selectedSequence !== MAIN_CANVAS_ID;
        },

        type() {
            return this.inlineBetweenAddData.type;
        },

        searchPlaceholder() {
            return this.displayWhens
                ? this.$t('funnel.inlineBetweenMenu.search.placeholder.when.or.then')
                : this.$t('funnel.inlineAddMenu.searchPlaceholderThen');
        },

        sourceElement() {
            return this.inlineBetweenAddData.sourceElement;
        },

        isBigCardEnabled() {
            return this.inlineBetweenAddData.internalZoomLevel >= BIG_CARD_THRESHOLD;
        },

        popoverStyle() {
            const { event, internalZoomLevel, stagePan, sourceElement } = this.inlineBetweenAddData;
            const container = event?.evt?.srcElement?.getBoundingClientRect();
            const { initialOffsetX, initialOffsetY } = this.calculateOffset(internalZoomLevel);
            const bigCardOffsetX = this.isBigCardEnabled ? (this.bigCardOffset) : 0;

            const x = ((sourceElement.mxGeometry.x + container.left) * internalZoomLevel + stagePan.x) + (initialOffsetX / internalZoomLevel) + bigCardOffsetX;
            const y = ((sourceElement.mxGeometry.y + container.top) * internalZoomLevel + stagePan.y) - (initialOffsetY / internalZoomLevel);

            return { zIndex: 50000, transform: `translate(${x}px, ${y}px)` };
        },

        whenElements() {
            return this.getCellElements(CELL_TYPE.WHEN);
        },

        thenElements() {
            return this.getCellElements(CELL_TYPE.THEN);
        },
    },

    mounted() {
        this.$nextTick(() => this.$refs.searchInput?.$el.querySelector('input')?.focus());
    },

    methods: {
        getDecimalPartInternalZoomLevel(internalZoomLevel) {
            if (internalZoomLevel >= 1) {
                return Math.round((internalZoomLevel % 1) * 10);
            }

            return 0;
        },

        calculateOffset(internalZoomLevel, baseX = OFFSET_BASE_X, baseY = OFFSET_BASE_Y, multiplierX = OFFSET_MULTIPLIER_X, multiplierY = OFFSET_MULTIPLIER_Y) {
            const initialOffsetX = baseX - (multiplierX * this.getDecimalPartInternalZoomLevel(internalZoomLevel));
            const initialOffsetY = baseY + (multiplierY * this.getDecimalPartInternalZoomLevel(internalZoomLevel));

            return { initialOffsetX, initialOffsetY };
        },

        validCells(cellType) {
            if (cellType in VALID_INCOMING_FLOW) {
                if (this.sourceElement.children.length === 0) return false;
                const validChildren = [];

                this.sourceElement.children.forEach((child) => {
                    if (isValid(child.type, cellType)) {
                        validChildren.push(child);
                    }
                });

                return validChildren.length > 0;
            }

            return true;
        },

        isAWhen(element) {
            return WHEN_CELL_OPTIONS.some((option) => option.name === element.type);
        },

        clearSearch() {
            this.searchText = '';
        },

        getCellElements(type) {
            const isWhen = type === CELL_TYPE.WHEN;
            const options = (isWhen && !this.isInSequence) ? WHEN_CELL_OPTIONS : THEN_CELL_OPTIONS;
            const searchWords = this.searchText.toLowerCase().trim().split(' ');

            return options
                .filter((cell) => this.enabledCellTypes.includes(cell.name))
                .filter((cell) => {
                    if (this.isInSequence && [PROCESS_EMAIL_CONFIRM, SEQUENCE_START].includes(cell.name)) return false;
                    if (cell.name === SEQUENCE_START) return false;
                    if (!isValidTimerNextFlow(this.sourceElement.type, cell.name)) return false;
                    if (!this.validCells(cell.name)) return false;
                    if (!this.searchText) return true;
                    const label = this.$t(`funnel.sidebar.${type}.cell.${cell.name}.label`);

                    return searchWords.every((w) => (`${cell.name} ${label}`.toLowerCase().includes(w)));
                })
                .map((cell) => ({
                    ...cell,
                    displayLabel: cell.label || this.$t(`funnel.sidebar.${type}.cell.${cell.name}.label`),
                    iconUrl: cell.iconUrl || getIconPathByCellType(cell.name, isWhen ? 'green' : 'blue'),
                }));
        },

        onElementClick(element) {
            this.$emit('add-cell-between', element);
        },
    },
};
</script>


<style scoped lang="scss">
.list-group {
    margin-top: $spacing-250;
}
.headline-4 {
    margin-bottom: $spacing-150;
    color: $color-text-subtle;
    font-size: $font-size-sm;
    font-style: normal;
    font-weight: $font-weight-semibold;
    line-height: $line-height-xs;
}
.element-list {
    padding-left: $spacing-125;
    max-height: px-to-rem(300px);
    overflow-y: auto;
    overflow-x: hidden;
}
.element-icon {
    width: px-to-rem(24px);
    max-height: px-to-rem(24px);
}

.element-list-item {
    --list-item-padding: 0;
    padding-left: $spacing-100;
    cursor: pointer;
    border-radius: $border-radius;

    &:hover {
        background-color: $color-gray-100;
    }
}

.element-search {
    margin: 0;
    border-bottom: 1px solid $color-gray-300;
    --input-border: 0;
}

.clear-icon {
    --icon-size: #{$icon-size-small};
    --icon-color: gray;
    cursor: pointer;
}

.inline-add-popover-menu ::v-deep {
    .list-item-info {
        border: none;
        padding: 0;
    }

    .popover .title {
        background: none;
        border: none;
        padding: $spacing-200 0;
    }
}

</style>
